<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  isVisible: { type: Boolean, default: false }
})

const emit = defineEmits(['close'])

const processes = ref([])
const loading = ref(false)
const error = ref('')
let refreshTimer = null

const agentProcesses = computed(() =>
  processes.value.filter(process =>
    /cursor-agent|\/agent\b|worker-server|subagent|agentic_flow/i.test(process.command || '')
  )
)

const summary = computed(() => ({
  agents: agentProcesses.value.filter(process => process.kind === 'agent').length,
  workers: agentProcesses.value.filter(process => process.kind === 'worker-server').length,
  subagents: agentProcesses.value.filter(process => process.kind === 'subagent').length
}))

function formatPercent(value) {
  return Number(value || 0).toFixed(1)
}

async function fetchAgentProcesses() {
  loading.value = true
  error.value = ''

  try {
    const { data } = await axios.get('/api/monitoring-tool/agent-processes')
    processes.value = data.processes || []
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Failed to load agent processes'
  } finally {
    loading.value = false
  }
}

function startRefresh() {
  stopRefresh()
  fetchAgentProcesses()
  refreshTimer = setInterval(fetchAgentProcesses, 5000)
}

function stopRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

function closeOverlay() {
  stopRefresh()
  emit('close')
}

watch(
  () => props.isVisible,
  visible => {
    if (visible) {
      startRefresh()
    } else {
      stopRefresh()
    }
  },
  { immediate: true }
)

onBeforeUnmount(stopRefresh)
</script>

<template>
  <div v-if="isVisible" class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
    <div class="flex h-[78vh] w-[min(1100px,96vw)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
      <div class="flex items-center justify-between border-b border-gray-200 bg-gray-900 px-5 py-3 text-white">
        <div>
          <h2 class="text-lg font-semibold">Agent processes</h2>
          <p class="text-xs text-gray-400">Live Cursor agent, worker-server, and subagent processes</p>
        </div>
        <button class="p-2 text-gray-400 hover:text-white" @click="closeOverlay">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="grid grid-cols-3 gap-3 border-b border-gray-200 bg-gray-50 p-4 text-sm">
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Agents</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ summary.agents }}</div>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Worker servers</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ summary.workers }}</div>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white p-3">
          <div class="text-xs uppercase tracking-wide text-gray-500">Subagents</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ summary.subagents }}</div>
        </div>
      </div>

      <div class="flex items-center justify-between border-b border-gray-200 px-4 py-2">
        <div class="text-sm text-gray-600">
          <span v-if="loading">Refreshing…</span>
          <span v-else>{{ agentProcesses.length }} active process{{ agentProcesses.length === 1 ? '' : 'es' }}</span>
          <span v-if="error" class="ml-3 text-red-600">{{ error }}</span>
        </div>
        <button class="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50" @click="fetchAgentProcesses">
          Refresh
        </button>
      </div>

      <div class="flex-1 overflow-auto">
        <table class="min-w-full text-left text-xs">
          <thead class="sticky top-0 bg-gray-100 text-gray-600">
            <tr>
              <th class="px-3 py-2 font-medium">Kind</th>
              <th class="px-3 py-2 font-medium">PID</th>
              <th class="px-3 py-2 font-medium">User</th>
              <th class="px-3 py-2 text-right font-medium">CPU%</th>
              <th class="px-3 py-2 text-right font-medium">MEM%</th>
              <th class="px-3 py-2 font-medium">Elapsed</th>
              <th class="px-3 py-2 font-medium">Command</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="process in agentProcesses" :key="process.pid" class="border-b border-gray-100 hover:bg-pink-50/30">
              <td class="px-3 py-2">
                <span class="rounded-full bg-pink-100 px-2 py-0.5 text-[11px] font-medium text-pink-700">
                  {{ process.kind }}
                </span>
              </td>
              <td class="px-3 py-2 font-mono text-gray-700">{{ process.pid }}</td>
              <td class="px-3 py-2">{{ process.user }}</td>
              <td class="px-3 py-2 text-right font-mono" :class="process.cpu >= 10 ? 'text-orange-600' : 'text-gray-700'">
                {{ formatPercent(process.cpu) }}
              </td>
              <td class="px-3 py-2 text-right font-mono">{{ formatPercent(process.mem) }}</td>
              <td class="px-3 py-2 font-mono text-gray-500">{{ process.etime }}</td>
              <td class="max-w-[520px] truncate px-3 py-2 font-mono text-gray-700" :title="process.command">
                {{ process.command }}
              </td>
            </tr>
            <tr v-if="!loading && agentProcesses.length === 0">
              <td colspan="7" class="px-3 py-8 text-center text-sm text-gray-500">
                No Cursor agent processes detected.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
